
interface ProviderStatus {
  is_approved: boolean;
  is_active: boolean;
  subscription_status: string;
  workwear_purchased: boolean;
  documents_uploaded: boolean;
  verification_status?: string;
}

interface ProviderStatusBannerProps {
  status: ProviderStatus;
}

function StatusItem({ label, completed }: { label: string; completed: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center ${
          completed ? 'bg-green-500' : 'bg-gray-300'
        }`}
      >
        {completed ? (
          <i className="ri-check-line text-white text-xs"></i>
        ) : (
          <i className="ri-close-line text-white text-xs"></i>
        )}
      </div>
      <span className="text-xs font-medium text-gray-700">{label}</span>
    </div>
  );
}

export default function ProviderStatusBanner({ status }: ProviderStatusBannerProps) {
  const isFullyActive =
    status.is_approved &&
    status.is_active &&
    status.subscription_status === 'active' &&
    status.workwear_purchased &&
    status.documents_uploaded;

  if (isFullyActive) {
    return (
      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-white text-xl"></i>
          </div>
          <div>
            <h3 className="font-semibold text-green-900">Fully Active</h3>
            <p className="text-sm text-green-700">
              You're all set! You can now receive and accept jobs.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusMessage = () => {
    if (!status.subscription_status || status.subscription_status !== 'active') {
      return {
        title: 'Payment Pending',
        message: 'Complete your subscription payment to activate your account.',
        icon: 'ri-bank-card-line',
        color: 'red',
        action: 'Complete Payment',
        link: '/provider-dashboard?tab=subscription',
      };
    }

    if (status.verification_status === 'pending' || status.verification_status === 'in_progress') {
      return {
        title: 'Verification Pending',
        message: 'Your identity verification is being processed. This usually takes 24-48 hours.',
        icon: 'ri-shield-check-line',
        color: 'yellow',
        action: null,
        link: null,
      };
    }

    if (!status.documents_uploaded) {
      return {
        title: 'Documents Pending',
        message: 'Upload required documents to complete your profile.',
        icon: 'ri-file-upload-line',
        color: 'orange',
        action: 'Upload Documents',
        link: '/provider-dashboard?tab=profile',
      };
    }

    if (!status.workwear_purchased) {
      return {
        title: 'Workwear Required',
        message: 'Purchase your starter workwear kit to get activated.',
        icon: 'ri-t-shirt-line',
        color: 'blue',
        action: 'Buy Workwear',
        link: '/provider-dashboard?tab=workwear',
      };
    }

    if (!status.is_approved) {
      return {
        title: 'Approval Pending',
        message: "Your application is under review. We'll notify you once approved.",
        icon: 'ri-time-line',
        color: 'yellow',
        action: null,
        link: null,
      };
    }

    if (!status.is_active) {
      return {
        title: 'Account Inactive',
        message: 'Your account is currently inactive. Contact support for assistance.',
        icon: 'ri-error-warning-line',
        color: 'red',
        action: 'Contact Support',
        link: '/provider-dashboard?tab=support',
      };
    }

    return {
      title: 'Setup Required',
      message: 'Complete all requirements to start receiving jobs.',
      icon: 'ri-settings-3-line',
      color: 'gray',
      action: null,
      link: null,
    };
  };

  const statusInfo = getStatusMessage();
  const colorClasses = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-500',
      iconBg: 'bg-red-500',
      text: 'text-red-900',
      subtext: 'text-red-700',
      button: 'bg-red-500 hover:bg-red-600',
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-500',
      iconBg: 'bg-yellow-500',
      text: 'text-yellow-900',
      subtext: 'text-yellow-700',
      button: 'bg-yellow-500 hover:bg-yellow-600',
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-500',
      iconBg: 'bg-orange-500',
      text: 'text-orange-900',
      subtext: 'text-orange-700',
      button: 'bg-orange-500 hover:bg-orange-600',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      iconBg: 'bg-blue-500',
      text: 'text-blue-900',
      subtext: 'text-blue-700',
      button: 'bg-blue-500 hover:bg-blue-600',
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-500',
      iconBg: 'bg-gray-500',
      text: 'text-gray-900',
      subtext: 'text-gray-700',
      button: 'bg-gray-500 hover:bg-gray-600',
    },
  };

  const colors = colorClasses[statusInfo.color as keyof typeof colorClasses] || colorClasses.gray;

  return (
    <div className={`${colors.bg} border-l-4 ${colors.border} p-4 rounded-lg mb-6`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className={`w-10 h-10 ${colors.iconBg} rounded-full flex items-center justify-center`}>
            <i className={`${statusInfo.icon} text-white text-xl`}></i>
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold ${colors.text}`}>{statusInfo.title}</h3>
            <p className={`text-sm ${colors.subtext}`}>{statusInfo.message}</p>
          </div>
        </div>
        {statusInfo.action && statusInfo.link && (
          <a
            href={statusInfo.link}
            className={`${colors.button} text-white px-4 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap ml-4`}
          >
            {statusInfo.action}
          </a>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatusItem
            label="Subscription"
            completed={status.subscription_status === 'active'}
          />
          <StatusItem
            label="Verification"
            completed={status.verification_status === 'approved'}
          />
          <StatusItem label="Documents" completed={status.documents_uploaded} />
          <StatusItem label="Workwear" completed={status.workwear_purchased} />
          <StatusItem label="Approved" completed={status.is_approved} />
        </div>
      </div>
    </div>
  );
}
